/**
 * EQuIP Audit Query Hooks
 *
 * React Query hooks for provider audit list endpoints.
 * Wraps equipApi.dashboard.getProviderAudits with a typed key factory
 * and enabled guard for conditional parameters.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type { AuditListItem } from '@/lib/equip-types'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipAuditKeys = {
  all: () => ['equip', 'audits'] as const,
  providerAudits: (
    email: string,
    auditType: string,
    year: number,
    quarter: number
  ) =>
    ['equip', 'audits', 'provider', email, auditType, year, quarter] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches the audit list for a given provider, audit type, year, and quarter.
 * Query is disabled when email or auditType is null/undefined/empty.
 */
export function useProviderAudits(
  email: string | undefined,
  auditType: string | undefined,
  year: number,
  quarter: number
) {
  return useQuery<AuditListItem[]>({
    queryKey: equipAuditKeys.providerAudits(
      email ?? '',
      auditType ?? '',
      year,
      quarter
    ),
    queryFn: () =>
      equipApi.dashboard.getProviderAudits(email!, auditType!, year, quarter),
    enabled: !!email && !!auditType,
  })
}
